import { useId } from 'react'
import { IconoClaro, IconoOscuro, IconoSistema } from '../componentes/iconos'
import { ETIQUETAS, TEMAS, type Tema } from './tema'
import { useTema } from './useTema'

const ICONOS: Record<Tema, typeof IconoClaro> = {
  claro: IconoClaro,
  oscuro: IconoOscuro,
  sistema: IconoSistema,
}

interface Props {
  /**
   * En la barra lateral el rotulo se ve; en la fila de movil se deja solo para
   * lectores de pantalla, que ahi el sitio es el que es.
   */
  rotuloVisible?: boolean
  className?: string
}

/**
 * Conmutador de tema de tres estados.
 *
 * Son `<input type="radio">` de verdad, escondidos bajo su etiqueta, y no
 * botones con `aria-checked`: asi las flechas del teclado recorren el grupo, el
 * `<fieldset>` le pone nombre y no hay que reimplementar a mano el patron de
 * radiogroup. Lo que se ve es la etiqueta, que reacciona con `has-[:checked]`.
 *
 * El `name` sale de `useId` porque el armazon monta dos instancias -lateral y
 * movil- y dos grupos con el mismo nombre en el documento serian, para el
 * navegador, un unico grupo.
 *
 * Trazabis DS: los iconos de Lucide (sol, luna, monitor) acompanan a la
 * palabra, nunca la sustituyen: en la barra lateral se ven las dos cosas y en
 * la tira de movil, donde no hay sitio, la palabra queda para el lector de
 * pantalla. Seleccionado: fondo lavanda y tinta lila, como el item activo.
 */
export function ConmutadorTema({ rotuloVisible = true, className }: Props) {
  const { tema, cambiarTema } = useTema()
  const nombre = useId()

  return (
    <fieldset className={className}>
      <legend
        className={
          rotuloVisible
            ? 'text-[11px] font-semibold uppercase tracking-[0.1em] text-tinta-2'
            : 'sr-only'
        }
      >
        Tema
      </legend>

      <div
        className={`grid grid-cols-3 gap-1 rounded-lg border border-borde bg-superficie p-1 ${
          rotuloVisible ? 'mt-2' : ''
        }`}
      >
        {TEMAS.map((opcion) => {
          const Icono = ICONOS[opcion]
          return (
            <label
              key={opcion}
              title={rotuloVisible ? undefined : ETIQUETAS[opcion]}
              className={[
                // En la lateral (240 px) el icono va encima de la palabra: en
                // fila, «Sistema» no cabe con holgura en un tercio del ancho.
                rotuloVisible
                  ? 'flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1 text-[11.5px] font-semibold'
                  : 'flex min-h-7 min-w-8 items-center justify-center rounded-md px-1.5',
                'cursor-pointer transition-colors',
                'text-tinta-2 hover:bg-elevado hover:text-tinta',
                'has-[:checked]:bg-acento-suave has-[:checked]:text-acento-tinta',
                'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-1 has-[:focus-visible]:outline-foco',
              ].join(' ')}
            >
              <input
                type="radio"
                name={nombre}
                value={opcion}
                checked={tema === opcion}
                onChange={() => cambiarTema(opcion)}
                className="sr-only"
              />
              <Icono aria-hidden="true" className="size-3.5 shrink-0" />
              <span className={rotuloVisible ? '' : 'sr-only'}>{ETIQUETAS[opcion]}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
